import type {
	Dispatch,
	FC,
	MouseEvent,
	ReactNode,
	SetStateAction,
} from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import styled from "styled-components";
import type { IHelpsiteArticleContentFields } from "@types-contentful";
import {
	filterUrlPath,
	isAnchorOrDocumentLink,
	isFelixMobileUrl,
	richTextHelper,
} from "@helpers";
import {
	helpsiteTrackButton,
	helpsiteTrackLink,
} from "@helpers/helpsite-mixpanel";
import { BLOCKS, INLINES, type Block } from "@contentful/rich-text-types";
import { textToId } from "@utils/textToId";
import HelpArticleDropdown from "./HelpArticleDropdown";
import AppDownloadInfo from "./HelpInfoComponents/AppDownloadInfo";
import ContactSupportInfo from "./HelpInfoComponents/ContactSupportInfo";
import NetworkCoverageInfo from "./HelpInfoComponents/NetworkCoverageInfo";
import SimScanningTroubleInfo from "./HelpInfoComponents/SimScanningTroubleInfo";
import RememberInfo from "./HelpInfoComponents/RememberInfo";

const ContentBlock = styled.div`
	margin-bottom: 32px;
`;

const RichTextContent = styled.div`
	font-size: 18px;
	line-height: 1.6;
	color: #1a1a1a;
	margin: 24px 0;

	em,
	i {
		font-style: italic !important;
	}

	hr {
		border: 0;
		border-top: 1px solid #d6dbe5;
		margin: 24px 0;
	}

	table {
		width: 100%;
		margin: 20px 0;
		border-collapse: collapse;
	}

	th,
	td {
		border: 1px solid #d6dbe5;
		padding: 12px 14px;
		text-align: left;
		vertical-align: top;
	}

	th {
		background: #f6f8fb;
		font-family: var(--euclid-medium);
	}

	table p {
		margin: 0;
	}

	@media (max-width: 768px) {
		th,
		td {
			padding: 10px 12px;
			font-size: 16px;
		}
	}

	/* Prose lists: override global ol>li flex (markers, layout) and avoid marker clipping */
	ul,
	ol {
		display: block;
		width: 100%;
		box-sizing: border-box;
		margin: 0 0 16px;
		padding-inline-start: 1.5em;
	}

	ul {
		list-style-type: disc;
		list-style-position: outside;
		padding-inline-start: 1.5em;
	}

	ul ul {
		list-style-type: disc;
		margin-top: 8px;
		margin-bottom: 8px;
	}

	ol {
		list-style-type: decimal;
		list-style-position: outside;
		padding-inline-start: 1.5em;
	}

	ul > li,
	ol > li {
		display: list-item;
		margin: 8px 0;
		flex-direction: unset;
		counter-increment: none;
	}

	ul > li::marker,
	ol > li::marker {
		color: inherit;
		font-size: 1em;
	}

	ol > li::before {
		content: none !important;
		display: none !important;
		min-width: 0 !important;
	}

	li > p {
		display: block;
		margin: 0 0 0.35em 0;
		white-space: pre-line;
	}

	li > p:last-child {
		margin-bottom: 0;
	}

	/* Embedded rich-text images (see richText.js ImageContainer) */
	div img {
		max-width: 100%;
		height: auto;
	}

	/* Keep links inline with surrounding text, no line break */
	p a,
	p button {
		display: inline !important;
		white-space: normal !important;
	}
`;

const InfoComponentWrapper = styled.div`
	margin: 24px 0;
`;

const DropdownListWrapper = styled.div`
	border: 1px solid rgba(0, 0, 0, 0.8);
	margin: 24px 0;
	overflow: hidden;

	& > *:last-child {
		border-bottom: none !important;
	}
`;

interface HelpArticleContentProps {
	content?: IHelpsiteArticleContentFields | null;
	openDropdownSlug?: string | null;
	setOpenDropdownSlug?: Dispatch<SetStateAction<string | null>>;
}

/** Empty paragraphs collapse in HTML/CSS; preserve editor line breaks via spacing */
function paragraphIsWhitespaceOnly(block: Block): boolean {
	if (!block.content?.length) {
		return true;
	}
	return block.content.every((item) => {
		if (item.nodeType === "text") {
			return !("value" in item && item.value.replace(/\u00a0/g, " ").trim());
		}
		return false;
	});
}

const AccordionAnchor = styled.button.attrs({ type: "button" })`
	display: inline !important;
	background: none !important;
	border: none !important;
	box-shadow: none !important;
	outline: none !important;
	padding: 0 !important;
	margin: 0 !important;
	color: inherit !important;
	text-decoration: underline !important;
	cursor: pointer !important;
	font: inherit !important;
	line-height: inherit !important;
	transition: none !important;
	vertical-align: baseline !important;

	&:hover,
	&:active,
	&:focus {
		opacity: 1 !important;
		background: none !important;
		border: none !important;
		box-shadow: none !important;
		transform: none !important;
		transition: none !important;
	}
`;

const HelpArticleContent: FC<HelpArticleContentProps> = ({
	content,
	openDropdownSlug = null,
	setOpenDropdownSlug,
}) => {
	const router = useRouter();
	const pagePath = router.asPath.split("?")[0];

	if (!content) return null;

	const { addMoreInfoAboveText, richText, addMoreInfo, dropdownContent } =
		content;

	const trackRichTextLink =
		(href: string) => (e: MouseEvent<HTMLAnchorElement>) => {
			const label = e.currentTarget.textContent?.trim().slice(0, 200);
			helpsiteTrackLink({
				helpsite_section: "article_rich_text",
				page_path: pagePath,
				link_href: href,
				label: label || undefined,
			});
		};

	const handleAccordionAnchorClick = (href: string) => {
		if (!href.startsWith("#") || !setOpenDropdownSlug) return;
		const slug = href.slice(1).toLowerCase().replace(/_/g, "-");
		if (!slug) return;
		setOpenDropdownSlug(slug);
		requestAnimationFrame(() => {
			document.getElementById(slug)?.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		});
	};

	const richTextOptions = {
		renderNode: {
			[BLOCKS.PARAGRAPH]: (node: unknown, children: ReactNode) => {
				const block = node as Block;
				if (paragraphIsWhitespaceOnly(block)) {
					return (
						<p
							style={{ marginBottom: "16px", minHeight: "1.55em" }}
							aria-hidden="true"
						>
							&nbsp;
						</p>
					);
				}
				return (
					<p style={{ marginBottom: "16px", whiteSpace: "pre-line" }}>
						{children}
					</p>
				);
			},
			[INLINES.HYPERLINK]: (
				node: { data?: { uri?: string } },
				children: ReactNode,
			) => {
				const uri = node.data?.uri ?? "";
				const isAccordionAnchor =
					uri.startsWith("#") && uri.length > 1 && setOpenDropdownSlug;

				if (isAccordionAnchor) {
					return (
						<AccordionAnchor
							onClick={(e) => {
								const label = e.currentTarget.textContent?.trim().slice(0, 200);
								helpsiteTrackButton({
									helpsite_section: "article_rich_text",
									page_path: pagePath,
									link_href: uri,
									label: label || undefined,
									cta_type: "accordion_anchor",
								});
								handleAccordionAnchorClick(uri);
							}}
						>
							{children}
						</AccordionAnchor>
					);
				}
				if (uri.startsWith("#")) {
					return (
						<a href={uri} onClick={trackRichTextLink(uri)}>
							{children}
						</a>
					);
				}
				const isRelativePath = uri.startsWith("/");
				if (isRelativePath) {
					return (
						<Link href={uri} onClick={trackRichTextLink(uri)}>
							{children}
						</Link>
					);
				}
				if (isAnchorOrDocumentLink(uri)) {
					return (
						<a href={uri} onClick={trackRichTextLink(uri)}>
							{children}
						</a>
					);
				}
				const felixPath = isFelixMobileUrl(uri) ? filterUrlPath(uri) : uri;
				return (
					<Link href={felixPath} onClick={trackRichTextLink(uri)}>
						{children}
					</Link>
				);
			},
		},
	};

	const renderInfoComponent = (infoType: string | undefined) => {
		if (!infoType) return null;

		switch (infoType) {
			case "App Download":
				return <AppDownloadInfo />;
			case "Contact Support Team":
				return <ContactSupportInfo />;
			case "Network Coverage":
				return <NetworkCoverageInfo />;
			case "Having trouble scanning your SIM?":
				return <SimScanningTroubleInfo />;
			case "Remember":
				return <RememberInfo />;
			default:
				return null;
		}
	};

	/** Contentful uses "No widget" above and "No Widget" below (generated types). */
	const isNoWidgetChoice = (v: string) =>
		v === "No widget" || v === "No Widget";

	const aboveInfoComponent =
		addMoreInfoAboveText && !isNoWidgetChoice(addMoreInfoAboveText)
			? renderInfoComponent(addMoreInfoAboveText)
			: null;

	const belowInfoComponent =
		addMoreInfo && !isNoWidgetChoice(addMoreInfo)
			? renderInfoComponent(addMoreInfo)
			: null;

	return (
		<ContentBlock>
			{aboveInfoComponent && (
				<InfoComponentWrapper>{aboveInfoComponent}</InfoComponentWrapper>
			)}

			{richText?.nodeType && Array.isArray(richText?.content) && (
				<RichTextContent>
					{richTextHelper(richText, richTextOptions)}
				</RichTextContent>
			)}

			{belowInfoComponent && (
				<InfoComponentWrapper>{belowInfoComponent}</InfoComponentWrapper>
			)}

			{dropdownContent && dropdownContent.length > 0 && (
				<DropdownListWrapper>
					{dropdownContent.map((dropdown) => {
						const slug = textToId(dropdown.fields.title || "");
						return (
							<HelpArticleDropdown
								key={dropdown.sys.id}
								dropdown={dropdown.fields}
								dropdownId={slug}
								isOpen={openDropdownSlug !== null && slug === openDropdownSlug}
								onToggle={() =>
									setOpenDropdownSlug?.((prev) => (prev === slug ? null : slug))
								}
								openDropdownSlug={openDropdownSlug}
								setOpenDropdownSlug={setOpenDropdownSlug}
							/>
						);
					})}
				</DropdownListWrapper>
			)}
		</ContentBlock>
	);
};

export default HelpArticleContent;
